import * as React from "react";
import { useSelector } from "react-redux";
import {
  clearParameters,
  removeParameter,
  setDateParameters,
  setParameters,
} from "../redux/global/actions";
import {
  getLicenseDetails,
  getLicenses,
  getLicensesBulk,
} from "../redux/license/actions";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Button from "@mui/material/Button";
import MultiValueInput from "../inputs/multiValueInput";
import {
  Grid,
  Stack,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useEffect } from "react";
import "./Licenses.css";
import SearchDateInput from "../inputs/searchDateInput";
import {
  downloadCSV,
  downloadJSON,
  extractUserInfoFromToken,
  getLocalStorageSAMLToken,
} from "../utils";
import { DataTable, MaterialSymbol } from "@datalake/towercontrol-banner";
import ExportMenu from "../widgets/ExportMenu";

const LICENSE_COLUMNS = [
  { id: "license_id", label: "License ID", filter: "text", nowrap: true },
  { id: "creation_date", label: "Creation Date", nowrap: true },
  { id: "modification_date", label: "Modification Date", nowrap: true },
  { id: "type", label: "Type", filter: "select" },
  { id: "sold_to", label: "Sold To", filter: "text" },
  { id: "status", label: "Status", filter: "select" },
  { id: "orderId", label: "Order ID", filter: "text" },
];

export default function Licenses() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const queryParameters = useSelector((state) => state?.parameters?.licenses);

  const [currentLicenseId, setCurrentLicenseId] = React.useState();
  const [currentLicenseBusinessPartner, setCurrentLicenseBusinessPartner] =
    React.useState();
  const [currentLicenseOrderId, setCurrentLicenseOrderId] = React.useState();
  const [currentLicenseEndUser, setCurrentLicenseEndUser] = React.useState();

  const handleExecuteSearch = () => {
    let queryObject = {
      limit: 100,
    };
    if (queryParameters.licenseIds) {
      queryObject.license_id = queryParameters.licenseIds;
    }
    if (queryParameters.businessPartners) {
      queryObject.business_partner = queryParameters.businessPartners;
    }
    if (queryParameters.orderIds) {
      queryObject.order = queryParameters.orderIds;
    }
    if (queryParameters.endUsers) {
      queryObject.end_user = queryParameters.endUsers;
    }
    if (queryParameters.supportPartners) {
      queryObject.support_partner = queryParameters.supportPartners;
    }
    if (queryParameters.soldTos) {
      queryObject.sold_to = queryParameters.soldTos;
    }
    if (queryParameters.creationDateStart && queryParameters.creationDateEnd) {
      queryObject.creation_date_from = queryParameters.creationDateStart;
      queryObject.creation_date_to = queryParameters.creationDateEnd;
    }
    dispatch(getLicenses(queryObject));
  };

  const handleExecuteBulkSearch = () => {
    let queryObject = {};
    if (queryParameters.licenseIds) {
      queryObject.license_id = queryParameters.licenseIds;
    }
    if (queryParameters.businessPartners) {
      queryObject.business_partner = queryParameters.businessPartners;
    }
    if (queryParameters.orderIds) {
      queryObject.order = queryParameters.orderIds;
    }
    if (queryParameters.endUsers) {
      queryObject.end_user = queryParameters.endUsers;
    }
    if (queryParameters.supportPartners) {
      queryObject.support_partner = queryParameters.supportPartners;
    }
    if (queryParameters.soldTos) {
      queryObject.sold_to = queryParameters.soldTos;
    }
    if (queryParameters.creationDateStart && queryParameters.creationDateEnd) {
      queryObject.creation_date_from = queryParameters.creationDateStart;
      queryObject.creation_date_to = queryParameters.creationDateEnd;
    }
    const saml_token = getLocalStorageSAMLToken();
    const infoToken = extractUserInfoFromToken(saml_token);
    queryObject.email = infoToken.email;
    queryObject.request_user = infoToken.username;
    dispatch(getLicensesBulk(queryObject));
  };

  function handleSelectLicense(licenseId) {
    dispatch(getLicenseDetails(licenseId));
    navigate("/licenses/details/" + licenseId);
  }

  const handleDeleteLicenseId = (chipToDelete) => () => {
    dispatch(removeParameter({ licenses: { licenseIds: [chipToDelete] } }));
  };
  const handleClearLicenseIds = () => {
    dispatch(clearParameters({ licenses: "licenseIds" }));
  };
  const handleDeleteLicenseOrderId = (chipToDelete) => () => {
    dispatch(removeParameter({ licenses: { orderIds: [chipToDelete] } }));
  };
  const handleClearLicenseOrderIds = () => {
    dispatch(clearParameters({ licenses: "orderIds" }));
  };
  const handleDeleteLicenseEndUser = (chipToDelete) => () => {
    dispatch(removeParameter({ licenses: { endUsers: [chipToDelete] } }));
  };
  const handleClearLicenseEndUsers = () => {
    dispatch(clearParameters({ licenses: "endUsers" }));
  };
  const handleDeleteLicenseBusinessPartner = (chipToDelete) => () => {
    dispatch(
      removeParameter({ licenses: { businessPartners: [chipToDelete] } }),
    );
  };
  const handleClearLicenseBusinessPartners = () => {
    dispatch(clearParameters({ licenses: "businessPartners" }));
  };

  const licenses = useSelector((state) => {
    if (state.filteredLicenses) {
      return state.filteredLicenses.map((license) => {
        return {
          license_id: license?.license_id,
          modification_date: license?.modification_date,
          creation_date: license?.creation_date,
          description: license?.description,
          start_date: license?.start_date,
          end_date: license?.end_date,
          part_number: license?.part_number,
          quantity: license?.quantity,
          sold_to: license?.sold_to?.applicant_id,
          status: license?.status,
          type: license?.type,
          orderId: license?.orderId,
        };
      });
    } else return [];
  });

  const handleExport = (rows, format = "csv") => {
    const fields = LICENSE_COLUMNS.map((column) => ({ name: column.id }));
    if (format === "csv") {
      downloadCSV(fields, "licenses_export", ",", rows);
    } else {
      downloadJSON(fields, "licenses_export", rows);
    }
  };

  const [openBulkConfirmation, setOpenBulkConfirmation] = React.useState(false);
  const [openBulkMessage, setOpenBulkMessage] = React.useState(false);

  const handleKeydown = (event) => {
    if (event.key == "Enter") {
      event.stopPropagation();
      event.preventDefault();
      setTimeout(() => {
        handleExecuteSearch();
      }, 0);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);
    return () => {
      document.removeEventListener("keydown", handleKeydown);
    };
  }, []);

  return (
    <React.Fragment>
      <Dialog open={openBulkMessage} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="body2">
            Your query has been sent. You can follow the status in the Bulk
            Report page or wait for the Rainbow message that will announce the
            end of the export.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setOpenBulkMessage(false);
            }}
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openBulkConfirmation} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="body2">
            This query could take a few minutes, are you sure you want to
            continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              handleExecuteBulkSearch();
              setOpenBulkConfirmation(false);
              setOpenBulkMessage(true);
            }}
          >
            Yes
          </Button>

          <Button variant="text" onClick={() => setOpenBulkConfirmation(false)}>
            No
          </Button>
        </DialogActions>
      </Dialog>
      <MultiValueInput
        setCurrentParam={setCurrentLicenseId}
        setQueryParams={setParameters}
        currentParam={currentLicenseId}
        queryParams={queryParameters?.licenseIds}
        handleDelete={handleDeleteLicenseId}
        handleClear={handleClearLicenseIds}
        label="License ID"
        family="licenses"
        type="licenseIds"
      />
      <MultiValueInput
        setCurrentParam={setCurrentLicenseBusinessPartner}
        setQueryParams={setParameters}
        currentParam={currentLicenseBusinessPartner}
        queryParams={queryParameters?.businessPartners}
        handleDelete={handleDeleteLicenseBusinessPartner}
        handleClear={handleClearLicenseBusinessPartners}
        label="Business Partner"
        family="licenses"
        type="businessPartners"
      />
      <MultiValueInput
        setCurrentParam={setCurrentLicenseOrderId}
        setQueryParams={setParameters}
        currentParam={currentLicenseOrderId}
        queryParams={queryParameters?.orderIds}
        handleDelete={handleDeleteLicenseOrderId}
        handleClear={handleClearLicenseOrderIds}
        label="Order ID"
        family="licenses"
        type="orderIds"
      />
      <MultiValueInput
        setCurrentParam={setCurrentLicenseEndUser}
        setQueryParams={setParameters}
        currentParam={currentLicenseEndUser}
        queryParams={queryParameters?.endUsers}
        handleDelete={handleDeleteLicenseEndUser}
        handleClear={handleClearLicenseEndUsers}
        label="End User"
        family="licenses"
        type="endUsers"
      />
      <Grid>
        <SearchDateInput
          title={"Creation Date"}
          setFilterDate={setDateParameters}
          family="licenses"
          type="creationDateStart"
          type2="creationDateEnd"
        />
      </Grid>
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<MaterialSymbol name="search" size="button" />}
          onClick={handleExecuteSearch}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          startIcon={<MaterialSymbol name="cloud_download" size="button" />}
          onClick={() => setOpenBulkConfirmation(true)}
        >
          Bulk Export
        </Button>
      </Stack>
      {licenses.length > 0 && (
        <DataTable
          title="Licenses"
          subtitle={
            licenses.length === 100
              ? `${licenses.length} results, use bulk export for more`
              : `${licenses.length} results`
          }
          columns={LICENSE_COLUMNS}
          rows={licenses}
          getRowId={(row, i) => `${row?.license_id}-${i}`}
          onRowClick={(row) => handleSelectLicense(row?.license_id)}
          defaultRowsPerPage={25}
          size="small"
          actions={(rows) => (
            <ExportMenu
              disabled={!rows.length}
              onExport={(format) => handleExport(rows, format)}
            />
          )}
          sx={{ mt: 3 }}
        />
      )}
    </React.Fragment>
  );
}
